import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { AppConfirmDialog } from '../../../../components/AppConfirmDialog';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { UI_Z_CLASS } from '../../../../utils/uiZIndex';
import type { LeadActivity } from '../../../../pages/LeadDetailPage';

/** `future` keeps past/done occurrences as Plantafel history; `series` drops every occurrence. */
export type LeadActivityDeleteScope = 'future' | 'series';

type LeadActivityDeleteDialogProps = {
  activity: LeadActivity;
  onDelete: (scope?: LeadActivityDeleteScope) => Promise<string | null>;
  onClose: () => void;
};

const RECYCLE_HINT = 'Gelöschte Termine landen im Papierkorb und können 28 Tage lang wiederhergestellt werden.';

export function LeadActivityDeleteDialog({ activity, onDelete, onClose }: LeadActivityDeleteDialogProps) {
  const [pending, setPending] = React.useState<LeadActivityDeleteScope | 'single' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const overlayMouseDownRef = React.useRef(false);
  const busy = pending != null;
  const isSeries = Boolean(activity.series_id);

  const run = async (key: LeadActivityDeleteScope | 'single') => {
    if (busy) return;
    setPending(key);
    setError(null);
    const err = await onDelete(key === 'single' ? undefined : key);
    setPending(null);
    if (err) setError(err);
    else onClose();
  };

  React.useEffect(() => {
    if (!isSeries) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (!busy) onClose();
    };
    const resetOverlayPress = () => {
      overlayMouseDownRef.current = false;
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('mouseup', resetOverlayPress);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mouseup', resetOverlayPress);
    };
  }, [busy, isSeries, onClose]);

  if (!isSeries) {
    return (
      <AppConfirmDialog
        open
        onClose={onClose}
        title="Termin löschen?"
        description={
          <>
            „{activity.title}“ wird gelöscht. {RECYCLE_HINT}
            {error ? <span className="mt-2 block text-red-600">{error}</span> : null}
          </>
        }
        confirmLabel="In Papierkorb legen"
        destructive
        isLoading={busy}
        overlayClassName={UI_Z_CLASS.fullscreenModal}
        onConfirm={() => run('single')}
      />
    );
  }

  const total = activity.occurrence_count ?? activity.occurrence_ids?.length ?? 0;
  const done = activity.done_count ?? 0;
  const options: { key: LeadActivityDeleteScope; title: string; hint: string }[] = [
    {
      key: 'future',
      title: 'Nur offene zukünftige Termine',
      hint: 'Vergangene und erledigte Termine bleiben in Plantafel und Historie erhalten.',
    },
    {
      key: 'series',
      title: total > 0 ? `Gesamte Serie (${total} Termine)` : 'Gesamte Serie',
      hint:
        done > 0
          ? `Auch vergangene Termine, davon ${done} erledigt.`
          : 'Auch vergangene Termine.',
    },
  ];
  const titleId = 'lead-activity-delete-dialog-title';

  return createPortal(
    <div
      className={`fixed inset-0 ${UI_Z_CLASS.fullscreenModal} flex items-center justify-center bg-slate-900/45 p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-busy={busy}
      onMouseDown={(event) => {
        overlayMouseDownRef.current = event.target === event.currentTarget;
      }}
      onMouseUp={(event) => {
        if (busy) return;
        if (event.target === event.currentTarget && overlayMouseDownRef.current) onClose();
      }}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <h2 id={titleId} className="text-sm font-semibold text-app-text">
            Serie löschen
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-app-hover hover:text-app-text-muted disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="px-5 pb-4 text-sm leading-relaxed text-app-text-muted">
          „{activity.title}“ ist eine Serie. {RECYCLE_HINT}
        </p>
        <div className="space-y-2 px-5">
          {options.map((opt) => {
            const selected = pending === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={busy}
                onClick={() => void run(opt.key)}
                className={`flex w-full items-start justify-between gap-3 rounded-md border border-app-border px-3 py-2.5 text-left transition-colors ${
                  selected ? 'border-pipedrive-blue/40 bg-app-surface-muted' : busy ? 'opacity-40' : 'hover:bg-app-hover'
                }`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-app-text">{opt.title}</span>
                  <span className="mt-0.5 block text-xs text-app-text-subtle">{opt.hint}</span>
                </span>
                {selected ? <LoadingSpinner size="sm" className="mt-0.5 shrink-0" /> : null}
              </button>
            );
          })}
        </div>
        {error ? <p className="px-5 pt-3 text-sm text-red-600">{error}</p> : null}
        <div className="flex items-center justify-end gap-2 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="inline-flex h-9 items-center justify-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted transition-colors hover:bg-app-hover disabled:opacity-40"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
